import { useState, useEffect, useRef } from "react";
import type { Editor } from "@tiptap/react";

interface FloatBtnState {
  top: number;
  left: number;
  visible: boolean;
  insertPos: number;
}

interface TargetResult {
  targetEl: HTMLElement;
  insertPos: number;
}

function docOffsetAtChildIndex(editor: Editor, index: number): number {
  let pos = 0;
  for (let i = 0; i < editor.state.doc.childCount; i++) {
    if (i === index) {
      return pos;
    }
    pos += editor.state.doc.child(i).nodeSize;
  }
  return pos;
}

function findTargetByPosAtCoords(
  editor: Editor,
  pmRoot: HTMLElement,
  e: MouseEvent,
): TargetResult | null {
  try {
    const pos = editor.view.posAtCoords({ left: e.clientX, top: e.clientY });
    if (!pos) {
      return null;
    }
    const $pos = editor.state.doc.resolve(pos.pos);
    if ($pos.depth < 1) {
      return null;
    }
    const blockStart = $pos.before(1);
    const domInfo = editor.view.domAtPos(blockStart + 1);
    let el: HTMLElement | null = (
      domInfo.node.nodeType === Node.ELEMENT_NODE ? domInfo.node : domInfo.node.parentElement
    ) as HTMLElement | null;
    while (el && el.parentElement !== pmRoot) {
      el = el.parentElement;
    }
    if (!el) {
      return null;
    }
    return { insertPos: blockStart, targetEl: el };
  } catch {
    return null;
  }
}

function findTargetByElementFromPoint(
  editor: Editor,
  pmRoot: HTMLElement,
  e: MouseEvent,
): TargetResult | null {
  try {
    let el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    while (el && el.parentElement !== pmRoot) {
      el = el.parentElement;
    }
    if (!el || !pmRoot.contains(el)) {
      return null;
    }
    const pmChildren = [...pmRoot.children];
    const elIndex = pmChildren.indexOf(el);
    return { insertPos: docOffsetAtChildIndex(editor, elIndex), targetEl: el };
  } catch {
    return null;
  }
}

function verticalDistToRect(clientY: number, r: DOMRect): number {
  if (clientY < r.top) {
    return r.top - clientY;
  }
  if (clientY > r.bottom) {
    return clientY - r.bottom;
  }
  return 0;
}

function findNearestTargetByY(
  editor: Editor,
  pmRoot: HTMLElement,
  e: MouseEvent,
): TargetResult | null {
  try {
    const pmChildren = [...pmRoot.children] as HTMLElement[];
    let bestEl: HTMLElement | null = null;
    let bestDist = Infinity;
    for (const child of pmChildren) {
      const dist = verticalDistToRect(e.clientY, child.getBoundingClientRect());
      if (dist < bestDist) {
        bestDist = dist;
        bestEl = child;
      }
    }
    if (!bestEl) {
      return null;
    }
    return {
      insertPos: docOffsetAtChildIndex(editor, pmChildren.indexOf(bestEl)),
      targetEl: bestEl,
    };
  } catch {
    return null;
  }
}

function hideFloatBtnState(p: FloatBtnState): FloatBtnState {
  return { ...p, visible: false };
}

function makeShowBtnHandler(
  editorRef: React.RefObject<Editor | null>,
  hideTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>,
  setFloatBtn: React.Dispatch<React.SetStateAction<FloatBtnState>>,
) {
  return (e: MouseEvent) => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    clearTimeout(hideTimerRef.current);
    const pmRoot = editor.view.dom;
    const result =
      findTargetByPosAtCoords(editor, pmRoot, e) ??
      findTargetByElementFromPoint(editor, pmRoot, e) ??
      findNearestTargetByY(editor, pmRoot, e);
    if (!result) {
      return;
    }
    const rect = result.targetEl.getBoundingClientRect();
    const pmRect = pmRoot.getBoundingClientRect();
    setFloatBtn({
      insertPos: result.insertPos,
      left: pmRect.left - 32,
      top: rect.top + rect.height / 2 - 14,
      visible: true,
    });
  };
}

function makeHideBtnHandler(
  floatBtnElRef: React.RefObject<HTMLButtonElement | null>,
  hideTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>,
  setFloatBtn: React.Dispatch<React.SetStateAction<FloatBtnState>>,
) {
  return (e: MouseEvent) => {
    const btn = floatBtnElRef.current;
    if (btn && (e.relatedTarget === btn || btn.contains(e.relatedTarget as Node))) {
      return;
    }
    hideTimerRef.current = setTimeout(() => {
      setFloatBtn(hideFloatBtnState);
    }, 250);
  };
}

export function useFloatInsert(
  editorWrapperEl: HTMLDivElement | null,
  editorRef: React.RefObject<Editor | null>,
) {
  const [floatBtn, setFloatBtn] = useState<FloatBtnState>({
    insertPos: 0,
    left: 0,
    top: 0,
    visible: false,
  });
  const [floatMenuOpen, setFloatMenuOpen] = useState(false);
  const floatBtnElRef = useRef<HTMLButtonElement | null>(null);
  const pendingInsertPosRef = useRef<number | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!editorWrapperEl) {
      return;
    }
    const showBtn = makeShowBtnHandler(editorRef, hideTimerRef, setFloatBtn);
    const hideBtn = makeHideBtnHandler(floatBtnElRef, hideTimerRef, setFloatBtn);
    editorWrapperEl.addEventListener("mousemove", showBtn);
    editorWrapperEl.addEventListener("mouseleave", hideBtn);
    return () => {
      editorWrapperEl.removeEventListener("mousemove", showBtn);
      editorWrapperEl.removeEventListener("mouseleave", hideBtn);
    };
  }, [editorWrapperEl, editorRef]);

  return {
    floatBtn,
    floatBtnElRef,
    floatMenuOpen,
    hideTimerRef,
    pendingInsertPosRef,
    setFloatBtn,
    setFloatMenuOpen,
  };
}
